<template>
  <div
    class="viluu-gutschein"
    data-season="basis"
    :class="{ 'is-leaving': isLeaving, 'from-bfcache': fromBfcache }"
  >
    <div class="bg-blobs">
      <div class="blob b1"></div>
      <div class="blob b2"></div>
      <div class="blob b3"></div>
    </div>
    <div class="deco" id="deco" aria-hidden="true"></div>

    <main class="gs-main">

      <!-- ============================================================
           THRESHOLD — the dark anteroom. Nav and hero stand in it, the
           reading body below stays light. You do not land on a page but
           in front of a door; you step through at the seam on which the
           scratch card lies.
           ============================================================ -->
      <section class="schwelle">
        <!-- THIS NAV SITS INSIDE <main>, AND IT HAS DELIBERATELY BEEN LEFT THAT
             WAY. Clean would be a <header> BEFORE <main> — otherwise it shows
             up in a screen reader's landmark list as a child of the main
             content instead of before it. Pulling it out would mean detaching
             it from .schwelle, though: line width, inner spacing and the
             complete dark variant of the back button hang on .schwelle .nav
             and .schwelle .nav-back, and the dark zone surface with hairline
             and light shapes comes from .schwelle as well. The nav would then
             stand on a light ground above the dark zone.
             For a point of the "nice to have" sort that is the wrong price.
             Anyone rebuilding the threshold anyway takes it along: <header>
             with its own dark surface before <main>, then .schwelle can hand
             over the nav rules. -->
        <nav class="nav">
          <a href="/" class="logo" aria-label="Viluu — zur Startseite"><img src="/img/viluu/landing-v2/viluu-wordmark.svg" alt="viluu" /></a>
          <a class="nav-back" href="/">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            <span class="nav-back-text">Zurück</span>
          </a>
        </nav>

        <section class="gs-hero">
          <!-- LEFT COLUMN (grid 1–6): text only — brand, heading, value line,
               lead. What someone DOES here is next to it on the right in
               .gs-hero-geraet.
               The seam does NOT hang here: it sits on the grid child
               .gs-hero-geraet (see .schwelle .gs-hero-geraet in the
               stylesheet), because the device column determines the row
               height. Things may therefore be added below the lead without
               the zone edge jumping — this column just must not become
               taller than the device column. -->
          <div class="gs-hero-haupt">
            <div class="gs-mark">
              <span class="gs-mark-icon"><img src="/img/viluu/landing-v2/viluu-appicon.svg" alt="" width="48" height="48"></span>
              <!-- Season costume: only visible with a matching data-season -->
              <span class="gs-hat" aria-hidden="true">
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 40C10 40 14 14 34 12C50 10.4 56 22 52 30C49 36 40 38 40 38L10 40Z" fill="#C2334A" />
                  <ellipse cx="27" cy="42" rx="20" ry="7" fill="#FBF7F4" />
                  <circle cx="53" cy="27" r="7" fill="#FBF7F4" />
                </svg>
              </span>
            </div>

            <!-- „Viluu-Gutscheincode" hyphenated throughout: that is the house
                 spelling for brand compounds (agb.html „Viluu-Coins",
                 index.html „Viluu-Logo", „Viluu-Team"). In <title>, meta
                 description, og:title and twitter:title the separated form was
                 deliberately left standing — there it is the search query people
                 type, and that is a decision, not an oversight. -->
            <!-- 2026-08-10: „liegt direkt darunter" is gone. The client on the
                 wording: „die dicke Überschrift klingt iwie falsch. Dein
                 Secret Viluu Gutscheincode jetzt direkt freirubbeln oder
                 sowas in der art."
                 What changed and what did not: „Viluu-Gutscheincode" stays
                 hyphenated and stays in one piece — it is the search term
                 this page ranks for, and breaking it up would cost exactly
                 that. „Secret" comes in front of it, matching the code name
                 (SECRET20). The second half is now an INSTRUCTION instead of
                 a description: „liegt direkt darunter" said where the thing
                 is, „jetzt freirubbeln" says what to do with it, and that is
                 what the whole hero is built around.
                 The gradient sits on the instruction, not on the noun — the
                 coloured half of the line should be the half one acts on.
                 TWO SENTENCES, NOT AN EM DASH. „Gutscheincode — jetzt
                 freirubbeln" was the first attempt and typeset badly: with
                 text-wrap:balance the line broke right after the dash, so
                 the dash hung at the end of a line with nothing behind it.
                 A full stop gives the same pause and a break point that
                 holds at every width. -->
            <h1 class="gs-h1">Dein Secret Viluu-Gutscheincode. <span class="grad">Jetzt freirubbeln.</span></h1>

            <!-- Value line: says before scratching what this is about. It used to sit
                 only below the canvas — via Google you saw the discount only after scratching open. -->
            <!-- data-pct-gross and not data-pct: the line starts with the
                 value, „bis zu 20 % günstiger" would look here like a
                 forgotten capital letter. The three versions of the number
                 and when each applies are in the script at pctZahl/pct/pctGross. -->
            <p class="gs-wert"><b data-pct-gross="gesamt">Bis zu {{ discount }} %</b> Bonus, wenn du deine Coins kaufst</p>

            <!-- THE LEAD IS GONE — 2026-08-10, by the client's decision:
                 „Das kannst du wegnehmen. Dann rutscht das Handy weiter nach
                 oben und dann haben wir viel mehr View von der Hero."
                 It read: „Coins sind bei Viluu das Guthaben, mit dem du
                 Nachrichten schreibst — statt eines Abos. Der Code unter der
                 Folie senkt ihren Preis."
                 WHAT IT WAS FOR, so that nobody puts it back by accident and
                 nobody removes its replacement either: above the fold
                 everything is priced in coins — H1 and value line — and the
                 sentence was the only place that said what a coin is. „20 %
                 off something I don't know" is not an offer. It has to be
                 said, and it is still said — in „Was der Code dir bringt"
                 and in the FAQ „Was genau sind Coins?", both further down
                 the page.
                 THE RISK IS NAMED AND ACCEPTED: whoever arrives from Google,
                 scratches and leaves has then never read what they are
                 buying. If that ever needs answering again inside the hero,
                 it belongs in the value line — one clause, not a paragraph. -->
          </div>

          <!-- RIGHT COLUMN (grid 7–12): the action.
               Device with scratch field, copy, skip, below it the
               baseline and the next-campaign line.
               WHY A SECOND COLUMN AT ALL: with the device hanging below the
               text, #copyBtn sat at y 745 at every width and was cut off from
               1366×768 downwards; at the same time grid columns 10–12 were
               empty. The full calculation is at .gs-hero in the
               stylesheet.
               THE SEAM HANGS ON THIS ELEMENT (see --naht). Anyone appending
               something below here shifts nothing — the negative margin
               sits on the grid child, not on the last child. Anyone who
               moves the device elsewhere takes the zone edge along. -->
          <div class="gs-hero-geraet">

            <!-- THE SCRATCH FIELD SITS INSIDE A PHONE.
                 Not as decoration: it gives the primary button the most
                 credible place a button can have — anchored at the bottom
                 of the screen, above a home bar. And the page answers its
                 own most important question ("where do I enter the code?")
                 right in the hero instead of 1500 px further down.

                 The class .scratch-card STAYS on the device body: six rules
                 of the threshold layer hang on it (seam, light seam, the
                 clack on reveal, contact shadow, the focus-ring exception
                 for the bright island and the media query at 980). A new
                 name would cost all of them at once.

                 What the app would say goes into the device. What the PAGE
                 says to the visitor goes below the device (.sc-meta). -->
            <div class="scratch-card gs-phone">
              <!-- The device parts, all decoration and therefore all
                   aria-hidden: black display rim, four side buttons, the
                   pane of glass over the screen and the Dynamic Island.
                   The island comes LAST in the source although it sits at
                   the top of the picture — it has to lie above the glass,
                   and that is the layer order, not the reading order. -->
              <span class="gs-phone-rand" aria-hidden="true"></span>
              <span class="gs-phone-taste stumm" aria-hidden="true"></span>
              <span class="gs-phone-taste lauter" aria-hidden="true"></span>
              <span class="gs-phone-taste leiser" aria-hidden="true"></span>
              <span class="gs-phone-taste power" aria-hidden="true"></span>

              <div class="gs-phone-screen">
                <!-- The room for the notch pill. HERE STOOD A
                     STATUS BAR („9:41", signal bars, battery) — a 42 px dummy
                     in exactly the column where the primary button sits. It
                     was already excluded from being read out via aria-hidden,
                     so it told nobody anything and cost more height in return
                     than the copy button itself. What remains is the empty row
                     the pill stands on (reasoning at .gs-phone-notch in the
                     stylesheet). -->
                <div class="gs-phone-status" aria-hidden="true">
                  <span class="gs-phone-zeit">9:41</span>
                  <span class="gs-phone-funk">
                    <!-- Signal · WLAN · battery. Drawn, not a font: at 11 px a
                         glyph set would hang on a system font that renders
                         differently on every platform. The battery is built
                         the way Apple builds it — outline, nub, part fill —
                         and its fill stands at 20 %, see the status bar. -->
                    <svg width="17" height="11" viewBox="0 0 17 11" fill="currentColor"><rect x="0" y="7" width="3" height="4" rx="1"></rect><rect x="4.5" y="5" width="3" height="6" rx="1"></rect><rect x="9" y="2.5" width="3" height="8.5" rx="1"></rect><rect x="13.5" y="0" width="3" height="11" rx="1"></rect></svg>
                    <svg width="16" height="11" viewBox="0 0 16 11" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M1.5 3.9a10 10 0 0 1 13 0"></path><path d="M3.9 6.7a6.5 6.5 0 0 1 8.2 0"></path><path d="M6.4 9.3a3 3 0 0 1 3.2 0"></path></svg>
                    <svg width="25" height="12" viewBox="0 0 25 12"><rect x="0.5" y="0.5" width="21" height="11" rx="3.5" fill="none" stroke="currentColor" opacity=".4"></rect><path d="M23 3.8v4.4c.9-.3 1.4-1.1 1.4-2.2s-.5-1.9-1.4-2.2Z" fill="currentColor" opacity=".4"></path><rect x="2" y="2" width="11" height="8" rx="2" fill="currentColor"></rect></svg>
                  </span>
                </div>

                <!-- App header row. The title is the previous door-sign label —
                     element and id stay, only the typography changes. That is
                     the trick with which NO app is invented here: the label is
                     already named the way an app screen would be named. -->
                <header class="gs-app-kopf">
                  <span class="gs-app-back" aria-hidden="true">
                    <!-- 2.5 instead of 2.2: 17 px display size · 1.8 * 24 / 17 = 2.54.
                         The same optical stroke as CTA icon and benefit icons. -->
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                  </span>
                  <p class="sc-label" id="seasonKicker">Rubbelfeld</p>
                  <span class="gs-app-dots" aria-hidden="true">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg>
                  </span>
                </header>

                <div class="gs-app-koerper">
                  <div class="sc-stage" id="stage">
                    <!-- THERE IS NO aria-hidden HERE. It belongs here as long as the
                         foil lies — otherwise screen readers read „20 % sparen —
                         SECRET20 — Einlösbar…" → „Bild: Rubbelfeld, verdeckt deinen
                         Gutscheincode" → „Knopf, Erst freirubbeln, nicht
                         verfügbar": three statements about the same thing, two of
                         them wrong. It is therefore set by paintCover(), that is
                         in the same line in which the foil actually comes into
                         being, and removed again by reveal().
                         IN THE MARKUP IT WOULD BE WRONG: without JavaScript and
                         whenever paintCover() aborts (stage below 40 px), there
                         is no foil — the code then stands there visibly, „Erst
                         freirubbeln" stays locked forever, and a static
                         aria-hidden would take it permanently out of the read-out
                         flow. A state that hangs on a drawing must be set where
                         the drawing happens.
                         Without consequence for search: aria-hidden takes nothing
                         out of the indexable DOM, the code remains in the source. -->
                    <div class="sc-prize">
                      <span class="amount" id="prizeAmount">Bis zu {{ discount }} % sparen</span>
                      <!-- The token: the code does not lie there as a line of text but
                           as an embossed plaque with tear-off perforation — you
                           find an object, not a character string. The code stays
                           real DOM text in the same element as before. -->
                      <span class="sc-ticket"><span class="code" id="prizeCode">{{ code }}</span></span>
                      <span class="hint" id="prizeHint">Einlösbar beim Kauf deiner Coins</span>
                    </div>
                    <canvas id="scratch" role="img" aria-label="Rubbelfeld — verdeckt deinen Gutscheincode. Mit Maus oder Finger darüberfahren, oder den Knopf darunter benutzen."></canvas>
                    <!-- The shimmer invitation over the untouched foil (see .sc-sheen) -->
                    <div class="sc-sheen" aria-hidden="true"></div>
                    <!-- The shavings that come off while scratching. Child of the
                         stage and not of the canvas: on reveal they are meant
                         to stay put and not swing away with the door leaf. -->
                    <div class="sc-splitter" id="splitter" aria-hidden="true"></div>
                  </div>

                  <div class="sc-progress" aria-hidden="true"><i id="progressBar"></i></div>
                </div>

                <!-- The action bar. .sc-actions stays as a class, it still carries
                     the column split of the two buttons.
                     Next to it stood `gs-app-leiste` — a leftover name from a
                     rename carried out only halfway, for which there was never
                     a rule either here or in viluu-base.css/viluu-signup.css
                     and which no script touched either. Removed, because a
                     class that seems to mean something and means nothing sends
                     the next person looking for a rule that does not exist. -->
                <div class="sc-actions">
                  <button class="sc-copy" id="copyBtn" type="button" disabled>
                    <!-- ONE SVG, THREE GROUPS, THREE STATES. The switching is done
                         by CSS alone (see .ic-* in the stylesheet); the script
                         only sets .ist-kopiert. Because the SVG has a fixed
                         size, the button width does not jump on the change —
                         only the label text changes it, and it always did.
                         18 px instead of 17: with 15 px text and 9 px gap 16/17
                         is too dainty, 22 too loud. stroke-width 2.4 at 18 px
                         display size gives optically 1.8 px — the same stroke
                         weight as the 22 px benefit icons (which are at 2). That
                         is the rule for all stroke icons on this page:
                         stroke-width = 1.8 * 24 / display size.
                         aria-hidden stays: the state is told by the text in
                         #copyLabel and the message in #live — never the icon. -->
                    <svg class="sc-copy-ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <!-- WAITING: THE COIN WITH THE EMBOSSED HEART.
                           It is, character for character, the same coin that
                           hangs as a cursor over the foil (see #scratch in the
                           stylesheet): ring outside, heart in the middle. The
                           pointer explains the button and the button the pointer.

                           WHY IT SITS CENTRED: before, a circle lay off-centre at
                           the bottom left and three diagonal strokes ran off to
                           the top right. At 18 px that was a comet — ball plus
                           motion trail. Motion strokes on a LOCKED button say
                           "currently loading" instead of "still closed"; so the
                           state statement tipped over into its opposite.

                           WHY FILLED AND NOT STROKED: at 18 px display size and
                           2.4 stroke width there remains between outer ring
                           (r 6.6) and embossing (r 2.6) a ring space of 1.6 units
                           — for a third stroke of 2.4 width there is no room in
                           it. Every scratch, every extra shape merged in the
                           render test with ring or embossing into a blob, and the
                           bare two circles read as a target. As an AREA it works
                           out: the ring is 2.5 units thick (so optically the same
                           stroke weight as the neighbouring icons), the heart is
                           an area and not an outline and therefore still carries
                           its silhouette even at 5 px width.
                           The ring is punched out of two circles via
                           fill-rule=evenodd; whoever makes it thicker must touch
                           both radii. -->
                      <g class="ic-muenze" fill="currentColor" stroke="none">
                        <path fill-rule="evenodd" d="M12 3.6a8.4 8.4 0 1 0 0 16.8 8.4 8.4 0 0 0 0-16.8Zm0 2.5a5.9 5.9 0 1 1 0 11.8 5.9 5.9 0 0 1 0-11.8Z" />
                        <path d="M12 11.52C12 9.48 8.6 9.48 8.6 11.35C8.6 13.22 12 14.07 12 15.09C12 14.07 15.4 13.22 15.4 11.35C15.4 9.48 12 9.48 12 11.52Z" />
                      </g>
                      <!-- READY and COPIED: ONE token that stays put.
                           It is NEVER redrawn — do not touch it without
                           thinking of the success state.
                           THE NOTCHES ARE THE WHOLE ARGUMENT. Before, there stood
                           two offset rectangles plus a dashed vertical: character
                           for character the standard duplicate glyph, and with a
                           tick inside it a checkbox. A rectangle stays a
                           rectangle, no matter how sideways you lay it.
                           That is why the outline steps in on the left and right
                           at half height as a semicircle (r 2.2) — exactly the
                           feature by which you recognise the real token
                           .sc-ticket 40 px above in the picture. A notched
                           outline is no longer a rectangle and with a tick no
                           checkbox either. The former perforation (.ic-perfo) and
                           the cropped second token (.ic-zweite) have been dropped
                           without replacement: the notches do the same more
                           robustly, and "another one of those" is not the
                           statement here anyway, but "take this one with you".
                           Box 16.4 × 11.6 (1.41:1) — landscape like the pictured token. -->
                      <g class="ic-marke">
                        <path d="M5.4 8.2h11.6a2.4 2.4 0 0 1 2.4 2.4v1.2a2.2 2.2 0 0 0 0 4.4v1.2a2.4 2.4 0 0 1-2.4 2.4H5.4a2.4 2.4 0 0 1-2.4-2.4v-1.2a2.2 2.2 0 0 0 0-4.4v-1.2a2.4 2.4 0 0 1 2.4-2.4Z" />
                      </g>
                      <!-- COPIED: the tick draws itself into the standing token.
                           No free-floating check mark — that would drop the
                           object and turn one story into two alien pictures.
                           It deliberately sits right of centre and ends above
                           the notch height: at the notches the token is at its
                           narrowest inside, and tick arm and notch arc must not
                           touch at 18 px (recalculated distance between the
                           stroke edges: 0.5 left, 0.9 right, 0.5 bottom). -->
                      <g class="ic-haken">
                        <path d="M8.0 14.6 10.3 16.9 15.2 12.0" pathLength="1" stroke-dasharray="1" stroke-dashoffset="1" />
                      </g>
                    </svg>
                    <span id="copyLabel">Erst freirubbeln</span>
                  </button>
                  <button class="sc-skip" id="skipBtn" type="button">Ohne Rubbeln aufdecken</button>
                  <span class="gs-phone-home" aria-hidden="true"></span>
                </div>
              </div>
              <span class="gs-phone-glas" aria-hidden="true"></span>
              <span class="gs-phone-insel" aria-hidden="true"></span>
            </div>

            <!-- The baseline below the device. It speaks to the READER, it is
                 not an app surface — an app would not say that. -->
            <p class="sc-meta">Den Code gibst du im Shop ein, wenn du Coins kaufst.</p>

            <!-- THE ENTIRE CALENDAR IN ONE LINE.
                 Until just now nothing stood here — to the right instead
                 stood a 356 × 400 px white calendar card that pulled the eye
                 away from the scratch field and pushed the copy button below
                 the fold. What the returning visitor really needs from it is
                 ONE number: when is it worth it next time. That now stands
                 here, the grid stands at the end of the page (#kalender).

                 THE SEAM DOES NOT HANG ON THIS LINE but on the grid child
                 .gs-hero-geraet (see --naht in the stylesheet) — exactly why
                 it could be appended here at all and may disappear again
                 without side effects once no calendar entry is open any
                 more (the script then sets hidden).

                 The text in the markup is the state without JavaScript and
                 the one search engines see; the script rewrites occasion,
                 date and the day gap at runtime. The gap deliberately does
                 NOT sit in the source — a hand-tended number „in 84 Tagen"
                 would be wrong the day after. -->
            <p class="sr-only" role="status" aria-live="polite" id="live"></p>
          </div>

          <!-- HERE STOOD THE CAMPAIGN CALENDAR (grid columns 9–12).
               It now stands in the exit zone, BEFORE the terms of
               participation — the reasoning with all the measurements
               stands at .gs-hero and at .gs-kalender in the stylesheet. Up
               here it is represented by the line .gs-naechste below the
               device. The device has taken over its place. A third block
               does not belong here any more: the hero now carries exactly
               two things — what it is (left) and how to get it (right). -->

        </section>

        <!-- The light shaft: the slanted band in which the light falls through
             the anteroom. Sits behind the text via z-index, not above it. -->
        <div class="lichtkeil" aria-hidden="true"></div>

        <!-- The edge of the door: the big soft keyhole and the light
             hairline. Both sit on --naht-x so that they coincide with the
             brightest point of the zone surface. Purely decorative,
             hence aria-hidden and pointer-events:none. -->
        <div class="schluesselloch" aria-hidden="true"></div>
        <div class="kante" aria-hidden="true"></div>

        <!-- The light floor. Stands calmly below the edge until the code is
             revealed — then it rises and the door opens. After the hairline
             in the source so that the line above it stays sharp. -->
        <div class="tuerlicht" aria-hidden="true"></div>
      </section>

      <!-- From here on you are through. The continuation deliberately
           stands in the light already and no longer in the threshold: the
           card is the last element of the dark zone, otherwise it could not
           lie on the seam (see --naht in the stylesheet). -->
      <div class="gs-next" id="next">
        <a v-if="isLoggedIn" class="btn-primary" :href="portalHref">
          Zum Portal <span class="arrow">→</span>
        </a>
        <a v-else class="btn-primary" href="#einloggen" data-open-signup data-auth-mode="login">
          Einloggen &amp; Code einlösen <span class="arrow">→</span>
        </a>
        <p v-if="!isLoggedIn" class="sc-meta">Noch kein Konto?
          <a href="#registrieren" data-open-signup data-auth-mode="register">Kostenlos registrieren</a>
          — dauert unter zwei Minuten.</p>
      </div>

      <section class="gs-sec" id="einloesen">
        <div class="gs-sec-head">
          <h2>So löst du deinen Code <span class="grad">ein</span>.</h2>
          <p>Vier Schritte. Der Code wird nicht bei der Registrierung eingegeben, sondern erst
            dann, wenn du im Shop deine Coins kaufst.</p>
        </div>
        <div class="gs-steps">
          <div class="gs-step">
            <div class="n">01</div>
            <h3>Code freirubbeln</h3>
            <!-- „über das Rubbelfeld" and not „über das Feld": in the same list
                 of four, step 04 means by „Feld" the input field in the shop,
                 and on the rest of the page it does too („Du findest das Feld
                 nicht?"). Skimming, this sentence otherwise read like an
                 instruction at the input field. The word is introduced —
                 kicker in the phone, canvas aria-label, FAQ, campaign terms. -->
            <p>Fahr mit dem Finger oder der Maus über das Rubbelfeld. Hier wird nichts verlost, dahinter
              liegt für alle Besucher derselbe Aktionscode.</p>
          </div>
          <div class="gs-step">
            <div class="n">02</div>
            <h3>Einloggen</h3>
            <!-- THE JUMP LINK IS THE ANSWER TO THE ONE REAL BREAK IN THE
                 PAGE. Step 02 is called „Einloggen" — for anyone without an
                 account that is the registration hurdle, and in exactly that
                 moment "is this even worth it?" becomes acute. The answer
                 stands some 500 px further down and is not reachable there,
                 where the doubt arises. The link costs no area and shifts no
                 block; the section order of the reading body stays
                 untouched (it is right, see the comment at the
                 tear-off sheet). -->
            <p>Melde dich mit deinem Konto an.<span v-if="!isLoggedIn"> Noch keins? Die Registrierung ist
            kostenlos und dauert unter zwei Minuten.</span></p>
          </div>
          <div class="gs-step">
            <div class="n">03</div>
            <h3>Shop öffnen</h3>
            <p>Dort wählst du aus, wie viele Coins du haben möchtest. Sichtbar ist der Bereich erst, wenn du eingeloggt bist.</p>
          </div>
          <div class="gs-step">
            <div class="n">04</div>
            <h3>Code eingeben</h3>
            <!-- „werden abgezogen" and not „ziehen sich ab": „sich abziehen"
                 means in German "to peel off". Literally the sentence said the
                 20 % were peeling off the price — and that in the one line
                 that explains the saving. -->
            <p>Trag <strong data-code>{{ code }}</strong> ins Gutscheincode-Feld ein. Du bekommst <span data-pct="gesamt">bis zu {{ discount }} %</span> mehr Coins gutgeschrieben.</p>
          </div>
        </div>

        <div class="gs-where">
          <div class="gs-where-txt">
            <!-- ONE NAME FOR ONE FIELD. On this page it went by five versions
                 — „Gutscheincode-Feld", „Gutscheinfeld", „Feld für
                 Gutscheincodes", „ein Feld für den Gutscheincode", „das Feld
                 für deinen Code" —, in places twice differently in
                 consecutive sentences. Anyone searching for it in the shop
                 was thus given four search terms. Authoritative is the
                 version from the H3, <title> and meta description: „das
                 Gutscheincode-Feld". If it is called something else in the
                 app, its label applies — but then everywhere. The „er" in
                 the next sentence was also wrongly referenced: the heading
                 asks about the field (neuter), the answer answered about the shop. -->
            <h3>Wo finde ich das Gutscheincode-Feld?</h3>
            <p>Im <strong>Shop</strong>, wo du deine Coins kaufst. Sichtbar ist das Feld erst,
              wenn du eingeloggt bist. Deshalb kannst du den Code hier auf dieser Seite nicht
              eintragen, und deshalb taucht während der Registrierung auch kein Gutscheincode-Feld auf.</p>
            <p>Der Ablauf ist immer derselbe: einloggen, Shop öffnen, Coins auswählen und den Code
              in das Gutscheincode-Feld eintragen. Nach dem Bestätigen siehst du den reduzierten
              Preis, bevor du bezahlst.</p>
            <!-- The bar in front of this sentence was a quote mark on a sentence
                 that quotes nothing; the reasoning is at .gs-where-note. It is
                 now an aside with a surface and an icon of its own. -->
            <p class="gs-where-note">
              <span class="ic">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
                  <path d="M9.6 9.2a2.4 2.4 0 1 1 2.9 2.4v1.1" />
                  <path d="M12.5 15.2h.01" />
                </svg>
              </span>
              <span>Du findest das Gutscheincode-Feld nicht? Schreib an
              <a href="mailto:support@viluu.de">support@viluu.de</a> —
              am besten mit deinem Nickname und dem Code, dann sehen wir nach.</span>
            </p>
          </div>
          <!-- The mock-up demonstrates what the customer is about to do: type the
               code, press the button, price drops. The loop hangs on .gwv-run and
               starts only when the block is in view (see DEMONSTRATION below in
               the script). Without JS and with reduced motion exactly this markup
               stays — code in the field, reduced price next to it. That is the
               end state, that is, the state that is right without animation. -->
          <div class="gs-where-vis" aria-hidden="true">
            <div class="gwv-card">
              <!-- THE REDEEM ROW, arranged as in the shop: seal, field, button
                   in one line. What the customer recognises are those four
                   features — the seal, the horizontal arrangement, the
                   placeholder wording and the pink button. Everything else is
                   this page's own style. -->
              <div class="gwv-feld">
                <!-- The percent seal. Ten arcs on a computed circle, see
                     .gwv-ic — drawn by hand the scallops come out uneven, and
                     unevenness is exactly what one notices on a seal. -->
                <span class="gwv-ic">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round">
                    <path d="M12.00 4.60A2.45 2.45 0 0 1 16.35 6.01A2.45 2.45 0 0 1 19.04 9.71A2.45 2.45 0 0 1 19.04 14.29A2.45 2.45 0 0 1 16.35 17.99A2.45 2.45 0 0 1 12.00 19.40A2.45 2.45 0 0 1 7.65 17.99A2.45 2.45 0 0 1 4.96 14.29A2.45 2.45 0 0 1 4.96 9.71A2.45 2.45 0 0 1 7.65 6.01A2.45 2.45 0 0 1 12.00 4.60Z" />
                    <path d="M9.6 14.4 14.4 9.6" stroke-linecap="round" />
                    <circle cx="9.75" cy="9.75" r="1.25" /><circle cx="14.25" cy="14.25" r="1.25" />
                  </svg>
                </span>
                <!-- Three elements in the same grid cell: .gwv-ghost carries the
                     full code along invisibly, .gwv-ph the placeholder — the
                     wider of the two fixes the field width, so nothing jumps at
                     the first keystroke. .gwv-typed fills up character by
                     character in front of them. The ghost is filled via JS, not
                     in the markup — otherwise the code would stand twice in the
                     page and one of the two would go stale at the next campaign. -->
                <span class="gwv-eingabe">
                  <span class="gwv-slot">
                    <span class="gwv-ghost"></span>
                    <span class="gwv-ph">Gutschein-Code eingeben</span>
                    <span class="gwv-line"><span class="gwv-typed" data-code>{{ code }}</span><i class="gwv-caret"></i></span>
                  </span>
                </span>
                <span class="gwv-apply">Einlösen</span>
              </div>

              <!-- The shop answers a redeemed code, so the replica does too. -->
              <div class="gwv-ok">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M5 12.8 9.8 17.6 19 8.4" pathLength="1" />
                </svg>
                <span>Code angewendet</span>
              </div>

              <div class="gwv-result">
                <span class="gwv-res-label">Coin-Anzahl</span>
                <span class="gwv-werte">
                  <span class="gwv-old">100 %</span>
                  <span class="gwv-arrow">→</span>
                  <span class="gwv-new"><span data-pct-num="gesamt" style="display:none">{{ discount }}</span>{{ discount + 100 }} %</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- id="wert": target of the jump link from step 02 of the
           instructions. The reasoning stands there.
           tabindex="-1" as on the calendar: without it only the view jumps
           and the keyboard focus stays in step 02 — the next tab would then
           lead back up instead of into the block that you have just
           called up. -->


      <section class="gs-sec">
        <div class="gs-sec-head">
          <h2>Häufige <span class="grad">Fragen</span>.</h2>
        </div>
        <!-- The first two are open: they are the two questions visitors arrive
             with from search. Anyone who can read two answers straight away
             understands how the list works without a single click — and is
             more likely to open the third. -->
        <div class="gs-faq">
          <details>
            <summary>Wo gebe ich den Viluu-Gutscheincode ein?</summary>
            <p>Im <strong>Shop</strong>, wenn du deine Coins kaufst. Dort gibt es das
              Gutscheincode-Feld: Code eintragen, bestätigen, der reduzierte Preis steht sofort da.
              Wichtig: Du musst dafür eingeloggt sein. Bei der Registrierung gibt es kein
              Gutscheincode-Feld.</p>
          </details>
          <details>
            <summary>Bekomme ich mit dem Code gratis Coins?</summary>
            <p>Nein, der Code schenkt dir keine Coins. Er sorgt dafür, dass du für denselben Preis <strong>mehr Coins</strong> bekommst. Gratis-Coins gibt es bei Viluu über den <strong>Daily
              Bonus</strong>: App öffnen, das Pop-up bestätigen, 5 Coins werden gutgeschrieben,
              ganz ohne Code. Ein Anspruch auf den Bonus oder seine Höhe besteht nicht.</p>
            <!-- The proviso comes from § 2a Abs. 1 AGB (terms and conditions) and
                 reads the same on faq.html. It was missing here while the
                 promise „5 Coins“ stood there bare — a promise without the
                 proviso the AGB place on it is a different promise.
                 THE NUMBER 5 IS HARD-CODED IN THE MARKUP ON THIS PAGE. It is
                 correct today (identical to index.html and faq.html), but it
                 is a copy: percentage value and code come from AKTION via
                 [data-pct]/[data-code], this figure does not. Whoever
                 changes the Daily Bonus must update this page by hand.
                 THE PRICE SPAN 0,90–1,71 € IS DONE: it stood here and in the
                 tear-off sheet twice and now runs through
                 [data-preis-min]/[data-preis-max] from AKTION.preis. -->

          </details>
          <details>
            <summary>Bekomme ich beim Neuladen einen anderen Code?</summary>
            <p>Nein. Das Rubbelfeld verlost nichts, dahinter liegt für alle Besucher derselbe
              Aktionscode. Du kannst die Seite also gefahrlos neu laden, wenn du ihn verlegt hast.
              Einlösen lässt er sich trotzdem nur einmal pro Konto.</p>
          </details>
          <!-- THIS QUESTION ONLY EXISTS BECAUSE THERE ARE TWO CODES. The page
               permanently shows one code and a different one on campaign
               days (see block 1–3 in the script). Anyone who was here on
               Christmas Eve and comes back in January finds a different code
               and would otherwise take it for a bug. The answer says both:
               that it is worth coming now, and that nobody misses out.
               NO DATE AND NO PERCENTAGE IN THE TEXT: both go stale, and both
               already stand in places that fill themselves — the deadline in
               the campaign conditions, the value in the scratch field. -->
          <details>
            <summary>Wie lange gilt der Gutscheincode?</summary>
            <p>Der Code auf dieser Seite gilt dauerhaft, du kannst also jederzeit wiederkommen und
              ihn dir holen. Zu bestimmten Anlässen wie Halloween, Nikolaus, Weihnachten oder Silvester
              liegt hier für ein paar Tage ein eigener Aktionscode, oft mit einem besseren Vorteil.
              Läuft gerade so eine Aktion, steht ihr letzter Tag unten in den Aktionsbedingungen.
              Danach erscheint an dieser Stelle wieder der dauerhafte Code. Maßgeblich ist immer der
              Code, den du gerade freigerubbelt hast.</p>
          </details>
          <details>
            <summary>Gutscheincode, Rabattcode, Aktionscode: ist das dasselbe?</summary>
            <p>Bei uns ja. Egal wie du es nennst: Es ist ein Code, den du eingeloggt im Shop ins
              Gutscheincode-Feld einträgst. Er sorgt dafür, dass du für denselben Preis mehr Coins bekommst.
              Zusätzliche Coins ohne Kauf schreibt er dir nicht gut. Einen „Coin-Generator” oder unbegrenzte Gratis-Coins
              gibt es nicht, wer so etwas anbietet, gehört nicht zu Viluu.</p>
            <!-- The closing quotation mark was a straight ASCII inch sign. It is
                 the only quotation mark in the visible text of the page and
                 therefore instantly, visibly wrong. German quotation marks:
                 „ opening at the bottom, “ closing at the top. -->

          </details>
          <details>
            <summary>Muss ich etwas kaufen, damit der Code funktioniert?</summary>
            <p>Ja. Der Rabatt greift beim Kauf von Coins. Ohne Kauf gibt es nichts abzuziehen.
              Die Registrierung selbst bleibt kostenlos, du kannst dir also erst alles ansehen
              und später entscheiden.</p>
          </details>
          <details>
            <summary>Wie viel spare ich mit dem Code?</summary>
            <!-- THE SENTENCE DELIBERATELY NO LONGER STARTS WITH THE VALUE. What
                 stood here was „<span data-pct>20 %</span> auf den Preis
                 deiner Coins“ — with the „bis zu“ version that would have
                 become a sentence starting in lower case. Rather than put a
                 capitalised special version in a spot where it gains
                 nothing, the sentence was rearranged: it now carries the
                 value in the middle and answers in passing the question that
                 „bis zu“ raises in the first place — what it depends on. -->
            <p>Mit dem Code bekommst du <span data-pct="gesamt">bis zu  {{ discount }} %</span> mehr Coins für denselben Preis.
              <span data-nur-bis>Wie viele es genau sind, hängt vom Paket ab, den höchsten Bonus gibt
            es beim größten Paket.</span> Du siehst die Coin-Anzahl direkt im Shop, sobald du den Code
              einträgst, bevor du bezahlst.</p>
          </details>
          <details>
            <summary>Mein Code wird nicht angenommen. Woran liegt das?</summary>
            <p>Der Code ist mehrfach verwendbar. Wenn er nicht angenommen wird, liegt ein Fehler vor.
              Schreib in dem Fall an <a href="mailto:support@viluu.de">support@viluu.de</a>, am besten mit deinem
              Nickname und dem Code.</p>
          </details>
          <details>
            <summary>Kann ich mehrere Codes einlösen?</summary>
            <p>Den Code kannst du beliebig oft einlösen. Mit anderen Aktionscodes ist er nicht kombinierbar, der Shop erlaubt jeweils nur einen Code gleichzeitig.</p>
          </details>
          <details>
            <summary>Ich habe den Code vergessen. Wo finde ich ihn wieder?</summary>
            <p>Lade diese Seite neu und rubbel erneut frei. Solange die Aktion läuft, erscheint
              derselbe Code wieder.</p>
          </details>
        </div>
      </section>

    </main>

    <!-- ============================================================
         EXIT — you leave the room through the same door: participation
         conditions, end CTA and footer stand on a dark ground again. The
         mandatory notice therefore sits structurally on --sw-ink-mid and
         cannot end up in a grey zone again.

         <footer> AND NOT <section>: everything after </main> was until just
         now in NO landmark at all. An unnamed <section> is not a "region"
         for screen readers but a generic box — anyone navigating by landmark
         list (VoiceOver rotor, NVDA key D) jumped from main straight to the
         end of the document and missed the participation conditions, of all
         things the text that has to be findable. At the same time this page
         was the only one on the site without a contentinfo, which is where
         you otherwise find Impressum and Datenschutz.
         A top-level <footer> is contentinfo and solves both with one tag.
         That the end CTA thereby sits in the footer is the deliberately paid
         price: it is spatially there anyway, and a landmark with one button
         too many is better than three blocks in none.
         ============================================================ -->
    <footer class="ausgang">
      <!-- The same light shapes, mirrored: here the door is at the top. -->
      <div class="lichtkeil" aria-hidden="true"></div>
      <div class="schluesselloch" aria-hidden="true"></div>
      <div class="kante" aria-hidden="true"></div>


      <div class="gs-terms">
        <!-- h2 and not h3: this block is not a subsection of the FAQ but a
             section of its own. Reasoning at the selector .gs-terms h2. -->
        <!-- „Aktionsbedingungen“ and not „Teilnahmebedingungen“, „Anbieter“
             and not „Veranstalter“, „Einlösen können den Code…“ and not
             „Teilnahmeberechtigt sind…“: three lines further down the block
             says explicitly that the code is not a prize and that the
             scratch field raffles nothing — and was framed in prize-draw
             vocabulary all around. The wording thus claimed exactly what
             the content denies. The statements themselves are unchanged. -->
        <h2>Aktionsbedingungen</h2>
        <!-- role="list" is no duplication: Safari takes the list semantics
             away from a <ul> as soon as list-style:none is set — and that is
             what the rule for the hanging indent does. Without this line
             VoiceOver reads eight paragraphs instead of "list, eight items". -->
        <ul role="list">
          <li>Anbieter der Aktion ist die <strong>We2Gether GmbH</strong>, Friedrichstraße 171,
            10117 Berlin.
          </li>
          <!-- „Rabatt“ instead of „Nachlass“: the visible body text of the page
               says Rabatt throughout (FAQ, bubble, step 04). Here „Nachlass“
               stood twice in the same breath and the next bullet said
               „Rabatt“ again — three words for one benefit. -->
          <li><strong>Aktionsvorteil:</strong> Mehr Coins für denselben Preis. Mit dem Code wird beim Kauf
            eine höhere Coin-Anzahl gutgeschrieben. Es erfolgt keine Barauszahlung.
          </li>
          <li>Der Code ist kein Gewinn. Das Rubbelfeld verlost nichts,
            für alle Besucher wird derselbe Code angezeigt.
          </li>
          <li>Einlösung ausschließlich <strong>eingeloggt im Shop</strong> über das
            Gutscheincode-Feld, <strong>nicht bei der Registrierung</strong>. Sie setzt den Kauf von
            Coins voraus.
          </li>
          <li>Der Code ist <strong>beliebig oft einlösbar</strong>, nicht mit anderen Aktionscodes kombinierbar.
            Pro Person ist nur ein Profil zulässig; Mehrfachanmeldungen darf der Betreiber zurückweisen
            (§ 4 Abs. 3 <a href="https://viluu.de/agb">AGB</a>).
          </li>
          <li>Einlösen können den Code voll geschäftsfähige Personen <strong>ab 18 Jahren</strong>,
            die als Privatpersonen handeln. Alle Preise inklusive Mehrwertsteuer.
          </li>
          <!-- HERE STOOD THE NOTE "IF A DEADLINE IS FIXED: add a field
               gueltigBis to AKTION and fill this bullet via a data
               attribute". It has been redeemed — only in a different place
               than expected: the deadline does not hang off the code but off
               the calendar entry (`bis` in the campaign calendar), because
               how long a campaign runs is maintained there anyway. A second
               field for it would have been a second truth.
               The line is empty and hidden in the source: a date in the
               markup would be a false statement the day after, and of all
               places in the campaign conditions. It only becomes visible if
               a limited-time campaign really is running today — see
               "WHAT EXISTS ONLY DURING A LIMITED-TIME CAMPAIGN" in the
               script. If only the permanent code runs, the bullet below it
               stands, tying the entitlement to the campaign's visibility. -->
          <li id="fristZeile" hidden>Der aktuelle Aktionscode <strong data-code>{{ code }}</strong>
            gilt <strong>bis einschließlich
              <time></time>
            </strong>. Danach steht auf dieser Seite
            wieder der dauerhaft gültige Code.
          </li>
          <li>Solange die Aktion auf dieser Seite steht, besteht ein Anspruch auf den
            Aktionsvorteil, sofern die übrigen Bedingungen erfüllt sind. Der Betreiber kann die
            Aktion aus wichtigem Grund vorzeitig beenden, etwa bei technischen Störungen oder
            missbräuchlicher Nutzung, und macht das auf dieser Seite bekannt.
          </li>
          <li data-nur-bis>Die Höhe des Bonus hängt vom gewählten Coin-Paket ab; der auf dieser
            Seite genannte Satz ist der höchste erreichbare. Die genaue Coin-Anzahl siehst du im Shop,
            bevor du bezahlst.
          </li>
          <li>Ergänzend gelten die <a href="https://viluu.de/agb">AGB</a> und die
            <a href="https://viluu.de/datenschutz">Datenschutzerklärung</a>. Fragen zur Aktion beantwortet
            <a href="mailto:support@viluu.de">support@viluu.de</a>.
          </li>
        </ul>
      </div>

      <section v-if="!isLoggedIn" class="gs-band">
        <h2>Noch kein <span class="grad">Konto</span>?</h2>
        <p>Registrierung und Profil kosten nichts. Danach findest du den Shop
          in deinem Konto — und dort das Gutscheincode-Feld.</p>
        <a class="btn-primary" href="#registrieren" data-open-signup data-auth-mode="register">
          Kostenlos registrieren <span class="arrow">→</span>
        </a>
      </section>

      <div class="gs-foot">
        <!-- NO PLATFORM NOTICE IN THE FOOTER — a deliberate decision by the
             client. The paragraph (moderated profiles, no meetings, price per
             message) stands on index.html and faq.html and was explicitly
             removed here: the page is meant to stay focused on the voucher
             code. A round of corrections reported it as missing and put it
             back in — that was a step back behind a decision already taken,
             not a finding.

             What MUST be covered in substance is still on the page: the
             price span per message in the benefits and in the FAQ „Was genau
             sind Coins?“, the notice about fictitious profiles in the
             participation conditions, plus the AGB (terms) link below.
             Anyone who wants the paragraph back must clear that with the
             client — he knows the risk of a direct entry via Google. -->
        <div class="gs-foot-legal">
          <span>Copyright © 2026 | Viluu</span>
          <span class="trenner" aria-hidden="true">·</span>
          <a href="https://viluu.de/impressum">Impressum</a>
          <a href="https://viluu.de/agb">AGB</a>
          <a href="https://viluu.de/datenschutz">Datenschutz</a>
        </div>
      </div>
    </footer>

    <signup-modal />
    <cookie-banner />
  </div>
</template>

<script>
import { landingChromeMixin } from '../landingChrome';
import CookieBanner from '../../../../components/LandingV2/CookieBanner.vue';
import SignupModal from '../../../../components/LandingV2/signup/SignupModal.vue';

const DISCOUNT = 20;
const CODE = 'SECRET20';

// Decoration motifs per season. They were <template> elements in the delivered
// page; Vue compiles <template> away, so they live here as markup strings.
var MOTIVE = {
  basis: '<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12 7C9.5 4 5 4 3 8C1 12 5 15.5 12 20C19 15.5 23 12 21 8C19 4 14.5 4 12 7Z"/></svg>',
  weihnachten: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg"><path d="M12 2v20M2 12h20M5 5l14 14M19 5L5 19"/></svg>',
  blackfriday: '<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12 2l2.4 7.4H22l-6 4.4 2.3 7.2-6.3-4.6L5.7 21 8 13.8l-6-4.4h7.6z"/></svg>',
};

function initGutscheincodePage(GS_ROOT) {

  var DAUER = {
    season: 'basis',
    rabatt: DISCOUNT,
    art: 'bis',
    code: CODE,
    kicker: 'Rubbelfeld',
    preis: { min: '0,90 €', max: '1,71 €' },
    icon: ''
  };

  var SAISONS = ['basis', 'weihnachten', 'blackfriday'];

  function gsTag(iso) {
    var t = iso.split('-');
    return new Date(+t[0], +t[1] - 1, +t[2]);
  }

  var MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli',
    'August', 'September', 'Oktober', 'November', 'Dezember'];

  function gsLang(iso) {
    var d = gsTag(iso);
    return d.getDate() + '. ' + MONATE[d.getMonth()];
  }

  var HEUTE = (function () {
    var p = new URLSearchParams(location.search).get('datum');
    var d = (p && /^\d{4}-\d{2}-\d{2}$/.test(p)) ? gsTag(p) : new Date();
    if (isNaN(d.getTime())) {
      d = new Date();
    }
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  var TERMINE = [
    /* Halloween and Singles Day are POINTS: an evening and a date that
       carries its name. A span around 11.11. would take from the occasion
       exactly what makes it memorable. */
    { datum: '2026-10-31', anlass: 'Halloween' },
    { datum: '2026-11-11', anlass: 'Singles Day' },

    {
      datum: '2026-11-27', anlass: 'Black Friday', bis: '2026-11-30',
      bisAnlass: 'Cyber Monday', zusatz: '4 Tage, Freitag bis Montag'
    },

    { datum: '2026-11-29', anlass: '1. Advent', zusatz: 'liegt mitten im Black-Friday-Zeitraum' },
    /* Nikolaus: a point. The day carries its name itself, a span around
       it would dilute it. */
    { datum: '2026-12-06', anlass: 'Nikolaus' },

    {
      datum: '2026-12-24', anlass: 'Heiligabend', von: '2026-12-21', bis: '2026-12-26',
      anlassLang: 'Weihnachtsaktion', zusatz: '6 Tage, Höhepunkt an Heiligabend'
    },

    {
      datum: '2026-12-31', anlass: 'Silvester', bis: '2027-01-01',
      bisAnlass: 'Neujahr', zusatz: '2 Tage, läuft in den Januar'
    },
    /* Dating Sunday is by definition a Sunday — a point. */
    { datum: '2027-01-03', anlass: 'Dating Sunday' },
    /* Valentinstag 2027 is a Sunday. Entered as a point and not as a
       weekend span, as long as it is not settled whether a campaign hangs
       on it — a span without a campaign is a claim about nothing. */
    { datum: '2027-02-14', anlass: 'Valentinstag' }

  ];

  TERMINE = (function () {
    var ISO = /^\d{4}-\d{2}-\d{2}$/;
    var gut = [], ankertage = {};

    function meckern(text) {
      if (window.console && console.warn) {
        console.warn('Calendar: ' + text);
      }
    }

    Array.prototype.forEach.call(TERMINE, function (t) {
      var name = (t && t.anlass) ? '"' + t.anlass + '"' : 'An entry without anlass';

      if (!t || !t.datum || !t.anlass) {
        meckern(name + ' has no datum or no anlass — both are required. The entry is skipped.');
        return;
      }

      /* All three date fields against the same pattern. `von` and `bis`
         are optional, but if they are there, they have to be right. */
      var falsch = null;
      ['datum', 'von', 'bis'].forEach(function (feld) {
        if (t[feld] && !ISO.test(t[feld])) {
          falsch = falsch || (feld + ':\'' + t[feld] + '\'');
        }
      });
      if (falsch) {
        meckern(name + ' has a date in the wrong format (' + falsch
          + '). Expected YYYY-MM-DD with leading zeros, so \'2026-10-31\' and '
          + 'not \'31.10.2026\'. The entry is skipped — otherwise the whole '
          + 'calendar grid would render empty.');
        return;
      }

      if ((t.bis || t.datum) < (t.von || t.datum)) {
        meckern(name + ' ends before it starts (' + (t.von || t.datum) + ' to '
          + (t.bis || t.datum) + '). The entry is skipped.'
          + (t.code ? ' The code "' + t.code + '" would therefore never apply.' : ''));
        return;
      }

      if (t.rabatt !== undefined && t.rabatt !== null && t.rabatt !== ''
        && (typeof t.rabatt !== 'number' || !isFinite(t.rabatt))) {
        meckern(name + ' gives rabatt as ' + (typeof t.rabatt) + ' ("' + t.rabatt
          + '") — a plain number WITHOUT a percent sign is expected, so 20 and not \'20 %\'. '
          + 'The value is ignored.');
        delete t.rabatt;
      }

      if (ankertage[t.datum]) {
        meckern(name + ' uses the anchor day ' + t.datum + ', which "'
          + ankertage[t.datum] + '" already occupies. Grid and bubble can only show '
          + 'one of the two — the second overwrites the first silently. Change one of '
          + 'the two datum values or merge the entries.');
      }
      ankertage[t.datum] = t.anlass;

      gut.push(t);
    });

    return gut;
  })();

  function gsBis(art) {
    return ((art || DAUER.art) !== 'genau') ? 'bis zu ' : '';
  }

  var AKTION = (function () {
    var a = {}, k;
    for (k in DAUER) {
      if (Object.prototype.hasOwnProperty.call(DAUER, k)) {
        a[k] = DAUER[k];
      }
    }
    a.laeuft = null;

    var aktiv = [];
    Array.prototype.forEach.call(TERMINE, function (t) {
      if (!t.code) {
        return;
      }
      var start = gsTag(t.von || t.datum);
      var ende = gsTag(t.bis || t.datum);

      if (HEUTE >= start && HEUTE <= ende) {
        aktiv.push(t);
      }
    });

    if (!aktiv.length) {
      return a;
    }

    /* Started later beats started earlier, see the rule above. With the
       same start the peak day decides — the same ordering the calendar
       uses for its legend. */
    aktiv.sort(function (x, y) {
      var vx = x.von || x.datum, vy = y.von || y.datum;
      if (vx !== vy) {
        return vx < vy ? -1 : 1;
      }
      return x.datum < y.datum ? -1 : 1;
    });
    var treffer = aktiv[aktiv.length - 1];

    if (aktiv.length > 1 && window.console && console.warn) {
      console.warn('Campaign: ' + aktiv.length + ' campaign codes apply today at the same time ('
        + aktiv.map(function (t) {
          return t.anlass + '/' + t.code;
        }).join(', ')
        + '). Showing "' + treffer.code + '" (the one that started last). '
        + 'Separate the overlapping periods in TERMINE.');
    }

    ['code', 'rabatt', 'art', 'kicker', 'icon'].forEach(function (f) {
      if (treffer[f] !== undefined && treffer[f] !== null && treffer[f] !== '') {
        a[f] = treffer[f];
      }
    });

    if (treffer.season) {
      if (SAISONS.indexOf(treffer.season) !== -1) {
        a.season = treffer.season;
      } else if (window.console && console.warn) {
        console.warn('Campaign: "' + treffer.anlass + '" names the unknown season "'
          + treffer.season + '". Known seasons are: ' + SAISONS.join(', ')
          + '. Staying with "' + a.season + '".');
      }
    }

    a.laeuft = treffer;
    return a;
  })();

  /* Season can be overridden via URL — handy for the preview:
     gutschein.html?season=weihnachten */
  (function () {
    var p = new URLSearchParams(location.search).get('season');
    if (p && SAISONS.indexOf(p) !== -1) {
      AKTION.season = p;
    }
    GS_ROOT.setAttribute('data-season', AKTION.season);

    var pctZahl = AKTION.rabatt + ' %';
    var istBis = (gsBis(AKTION.art) !== '');
    var pct = gsBis(AKTION.art) + pctZahl;
    var pctGross = istBis ? 'Bis zu ' + pctZahl : pctZahl;

    var setText = function (id, wert) {
      var el = document.getElementById(id);
      if (el) {
        el.textContent = wert;
      }
    };

    setText('prizeCode', AKTION.code);
    setText('prizeAmount', pctGross + ' sparen');
    setText('seasonKicker', AKTION.kicker);
    setText('prizeHint', 'Einlösbar beim Kauf deiner Coins');

    /* Swap in the season icon. The class switches off the scale repair of
       the base icon — the Claude Design icons fill their area edge to edge
       by themselves, the enlargement would crop them. */
    if (AKTION.icon) {
      var mark = document.querySelector('.gs-mark-icon');
      var markImg = mark && mark.querySelector('img');
      if (markImg) {
        mark.classList.add('ist-saison');
        markImg.src = AKTION.icon;
      }
    }

    Array.prototype.forEach.call(document.querySelectorAll('[data-pct]'), function (el) {
      el.textContent = pct;
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-pct-gross]'), function (el) {
      el.textContent = pctGross;
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-pct-num]'), function (el) {
      el.textContent = String(AKTION.rabatt);
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-code]'), function (el) {
      el.textContent = AKTION.code;
    });

    if (!istBis) {
      Array.prototype.forEach.call(document.querySelectorAll('[data-nur-bis]'), function (el) {
        el.hidden = true;
      });
    }

    if (AKTION.laeuft) {
      var endeIso = AKTION.laeuft.bis || AKTION.laeuft.datum;

      var frist = document.getElementById('fristZeile');
      var fristZeit = frist && frist.querySelector('time');
      if (frist && fristZeit) {
        /* datetime machine-readable, text written out — the same
           separation as on every <time> in the calendar. */
        fristZeit.setAttribute('datetime', endeIso);
        fristZeit.textContent = gsLang(endeIso);
        frist.hidden = false;
      }

      var label = document.getElementById('naechstLabel');
      if (label) {
        label.textContent = 'Läuft gerade:';
      }
    }

    Array.prototype.forEach.call(document.querySelectorAll('[data-pct-neg]'), function (el) {
      el.textContent = '−' + pctZahl;
    });

    var faktorEl = document.querySelector('[data-faktor]');
    if (faktorEl) {
      var r = Number(AKTION.rabatt);
      if (r > 0 && r < 100) {
        faktorEl.textContent = '×' + (1 / (1 - r / 100)).toFixed(1).replace('.', ',');
      } else {
        var zahlBox = faktorEl.closest('.gs-perk-zahl');
        if (zahlBox) {
          zahlBox.style.visibility = 'hidden';
        }
      }
    }

    if (AKTION.preis) {
      Array.prototype.forEach.call(document.querySelectorAll('[data-preis-min]'), function (el) {
        el.textContent = AKTION.preis.min;
      });
      Array.prototype.forEach.call(document.querySelectorAll('[data-preis-max]'), function (el) {
        el.textContent = AKTION.preis.max;
      });
    }

  })();

  (function () {
    var REVEAL_AT = 0.5;

    var canvas = document.getElementById('scratch');
    var stage = document.getElementById('stage');
    var bar = document.getElementById('progressBar');
    var copyBtn = document.getElementById('copyBtn');
    var copyLbl = document.getElementById('copyLabel');
    var skipBtn = document.getElementById('skipBtn');
    var next = document.getElementById('next');
    var live = document.getElementById('live');
    if (!canvas || !stage) {
      return;
    }

    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    var revealed = false, drawing = false, moves = 0, last = null;

    var buehneW = 0, buehneH = 0;
    /* Zero mark of the freshly painted cover layer: anti-aliasing pushes
       individual edge pixels below the alpha threshold, and the progress
       should still start at exactly zero. */
    var basisRatio = 0;

    var IKON_BREITE = 30;
    var IKON_TAKT = 58;
    var IKON_DECK = .14;  /* unchanged from the heart grid — there was no  */
    var IKON_SCHATT = .3;   /* reason to rotate: the new motif carries 6,6 %
                             ink per cell, the old one 9,1 %. More parts,
                             less colour. */

    var ikonStempel = null, ikonSchluessel = '';

    /* Returns WHETHER it painted. The return value is not a luxury: it
       decides whether repaintIfResized() may book the stage dimensions
       as done (see there). */
    function paintCover() {

      var r = canvas.getBoundingClientRect();
      /* Below this width the field is not laid out yet — try again later. */
      if (r.width < 40 || r.height < 40) {
        return false;
      }
      buehneW = r.width;
      buehneH = r.height;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(r.width * dpr);
      canvas.height = Math.round(r.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      var cs = getComputedStyle(GS_ROOT);
      var a = cs.getPropertyValue('--sn-scratch-a').trim() || '#F17C08';
      var b = cs.getPropertyValue('--sn-scratch-b').trim() || '#C63E7C';
      var ink = cs.getPropertyValue('--sn-scratch-ink').trim() || 'rgba(255,255,255,.94)';

      var g = ctx.createLinearGradient(0, 0, r.width, r.height);
      g.addColorStop(0, a);
      g.addColorStop(1, b);
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, r.width, r.height);

      /* Metal bands: alternately lightened diagonal stripes */
      ctx.save();
      ctx.fillStyle = ink;
      [.10, .26, .47, .66, .85].forEach(function (p, i) {
        ctx.globalAlpha = i % 2 ? .045 : .07;
        ctx.save();
        ctx.translate(r.width * p, 0);
        ctx.rotate(.42);
        ctx.fillRect(-14, -r.height, i % 2 ? 22 : 34, r.height * 3);
        ctx.restore();
      });
      ctx.restore();

      /* Brushed stroke: fine vertical lines as on brushed metal */
      ctx.save();
      ctx.strokeStyle = ink;
      ctx.globalAlpha = .025;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (var bx = 2; bx < r.width; bx += 3) {
        ctx.moveTo(bx + .5, 0);
        ctx.lineTo(bx + .5, r.height);
      }
      ctx.stroke();
      ctx.restore();

      var ikonSchl = ink + '|' + b + '|' + IKON_BREITE + '|' + dpr;
      if (ikonSchluessel !== ikonSchl || !ikonStempel) {
        ikonStempel = {
          tinte: stempelIkon(ink, IKON_BREITE, dpr),
          schatten: stempelIkon(b, IKON_BREITE, dpr)
        };
        ikonSchluessel = ikonSchl;
      }

      ctx.font = '700 15px "Bricolage Grotesque", system-ui, sans-serif';
      var t1 = ctx.measureText('Jetzt freirubbeln').width;
      ctx.font = '500 11.5px "Inter Tight", system-ui, sans-serif';
      var t2 = ctx.measureText('mit Finger oder Maus').width;
      var halbText = Math.max(t1, t2) / 2;
      var ruheRx = Math.min(halbText + 26, r.width * .48);
      var ruheRy = Math.min(30, r.height * .30);

      ikonRaster(ctx, r.width, r.height, dpr, ruheRx, ruheRy);

      ctx.save();
      ctx.lineWidth = 1;
      ctx.strokeStyle = ink;
      ctx.globalAlpha = .22;
      ctx.beginPath();
      ctx.moveTo(3.5, r.height - 3.5);
      ctx.lineTo(3.5, 3.5);
      ctx.lineTo(r.width - 3.5, 3.5);
      ctx.stroke();
      ctx.strokeStyle = b;
      ctx.globalAlpha = .5;
      ctx.beginPath();
      ctx.moveTo(r.width - 3.5, 3.5);
      ctx.lineTo(r.width - 3.5, r.height - 3.5);
      ctx.lineTo(3.5, r.height - 3.5);
      ctx.stroke();
      ctx.strokeStyle = ink;
      ctx.globalAlpha = .13;
      ctx.setLineDash([3, 3]);
      ctx.strokeRect(8.5, 8.5, r.width - 17, r.height - 17);
      ctx.restore();

      /* Label, engraved instead of printed: one pixel of shadow under the
         type gives it the embossing of the hearts. */
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = '700 15px "Bricolage Grotesque", system-ui, sans-serif';
      ctx.fillStyle = b;
      ctx.globalAlpha = .55;
      ctx.fillText('Jetzt freirubbeln', r.width / 2, r.height / 2 - 8);
      ctx.fillStyle = ink;
      ctx.globalAlpha = .96;
      ctx.fillText('Jetzt freirubbeln', r.width / 2, r.height / 2 - 9);
      ctx.font = '500 11.5px "Inter Tight", system-ui, sans-serif';
      ctx.fillStyle = b;
      ctx.globalAlpha = .45;
      ctx.fillText('mit Finger oder Maus', r.width / 2, r.height / 2 + 13);
      ctx.fillStyle = ink;
      ctx.globalAlpha = .82;
      ctx.fillText('mit Finger oder Maus', r.width / 2, r.height / 2 + 12);
      ctx.restore();

      ctx.globalCompositeOperation = 'destination-out';

      basisRatio = clearedRatio();

      var prizeEl = stage.querySelector('.sc-prize');
      if (prizeEl && !revealed) {
        prizeEl.setAttribute('aria-hidden', 'true');
      }

      return true;
    }

    function stempelIkon(farbe, S, dpr) {
      var rand = 2;                                  /* Air for the anti-aliasing */
      var cv = document.createElement('canvas');
      cv.width = cv.height = Math.ceil((S + rand * 2) * dpr);
      var c = cv.getContext('2d');
      c.translate(rand * dpr, rand * dpr);
      c.scale(S / 100 * dpr, S / 100 * dpr);
      c.fillStyle = farbe;
      c.strokeStyle = farbe;
      c.lineCap = 'round';
      c.lineJoin = 'round';

      var kugel = Math.max(1.70, S * .074) * 100 / S;
      var loch = Math.max(1.35, S * .060) * 100 / S;

      c.beginPath();                                   /* left lobe */
      c.moveTo(9, 55);
      c.bezierCurveTo(4, 30, 22, 21, 46, 31);         /* outer edge */
      c.lineTo(47, 40);                                /* narrow head at the centre */
      c.bezierCurveTo(28, 36, 13, 42, 15, 58);         /* return edge along the heart's rim */
      c.closePath();
      c.fill();

      c.beginPath();                                   /* right lobe, shorter */
      c.moveTo(91, 55);
      c.bezierCurveTo(96, 30, 79, 22, 56, 31);
      c.lineTo(55, 40);
      c.bezierCurveTo(74, 36, 87, 42, 85, 58);
      c.closePath();
      c.fill();

      c.beginPath();
      c.moveTo(46, 36);
      c.quadraticCurveTo(51, 27, 53, 18);
      c.lineTo(59, 20);
      c.quadraticCurveTo(56, 30, 53, 39);
      c.closePath();
      c.fill();

      c.beginPath();
      c.moveTo(50, 49);
      c.bezierCurveTo(50, 36, 20, 33, 20, 55);
      c.bezierCurveTo(20, 74, 37, 84, 50, 100);
      c.bezierCurveTo(63, 84, 80, 74, 80, 55);
      c.bezierCurveTo(80, 33, 50, 36, 50, 49);
      c.closePath();
      c.fill();

      c.beginPath();
      c.moveTo(8 + kugel, 58);
      c.arc(8, 58, kugel, 0, 6.2832);
      c.moveTo(56 + kugel, 16);
      c.arc(56, 16, kugel, 0, 6.2832);
      c.moveTo(92 + kugel, 58);
      c.arc(92, 58, kugel, 0, 6.2832);
      c.fill();

      if (S >= 24) {
        var ho = Math.max(loch * .40, .55 * 100 / S);
        var hu = Math.max(loch * .58, .80 * 100 / S);
        c.globalCompositeOperation = 'destination-out';
        c.beginPath();
        c.arc(50, 63, loch, 0, 6.2832);
        c.moveTo(50 - ho, 64);
        c.lineTo(50 + ho, 64);
        c.lineTo(50 + hu, 80);
        c.lineTo(50 - hu, 80);
        c.closePath();
        c.fill();
      }
      return cv;
    }

    function ikonRaster(ctx2, w, h, dpr, ruheRx, ruheRy) {
      var sInk = ikonStempel.tinte, sSch = ikonStempel.schatten;
      var halb = sInk.width / 2 / dpr;                 /* half stamp edge in CSS px */
      /* One CSS pixel of embossing downwards, rounded to whole device
         pixels — the same depth as in the heart grid. */
      var praegung = Math.max(1, Math.round(dpr));
      var takt = IKON_TAKT;
      /* The center of the quiet zone is the center of the lettering, not
         the center of the canvas: the two lines sit one pixel lower
         together (see fillText further up). */
      var cx = w / 2, cy = h / 2 + 1;

      /* Set up in DEVICE PIXELS, because the stamps were built there.
         Drawing under the dpr transform of paintCover() would scale them
         a second time. */
      ctx2.save();
      ctx2.setTransform(1, 0, 0, 1, 0, 0);
      ctx2.globalCompositeOperation = 'source-over';

      var mVon = Math.floor(-0.5 - halb / takt);
      var mBis = Math.ceil((h + halb) / takt);
      for (var m = mVon; m <= mBis; m++) {
        var y = takt * (m + 0.5);
        /* Every second row offset by half a beat. The modulo has to be
           correct for negative m as well, otherwise the top row flips by
           half a beat and the seam would sit exactly on the upper edge. */
        var versatz = (((m % 2) + 2) % 2) ? 0.75 : 0.25;
        var nVon = Math.floor((-halb) / takt - versatz);
        var nBis = Math.ceil((w + halb) / takt - versatz);
        for (var n = nVon; n <= nBis; n++) {
          var x = takt * (n + versatz);
          var dx = x - cx, dy = y - cy;
          var dist = Math.sqrt(dx * dx + dy * dy);
          var f;
          if (dist < 1e-6) {
            f = 0;                                     /* exactly in the center */
          } else {
            /* Ellipse radius along the ray to the center of the motif. */
            var norm = Math.sqrt((dx / ruheRx) * (dx / ruheRx) + (dy / ruheRy) * (dy / ruheRy));
            var rIn = dist / norm;
            f = (dist - rIn) / takt;
            f = f < 0 ? 0 : (f > 1 ? 1 : f);
          }
          /* Below 2 % residual opacity there is nothing left to see — the call
             would be pure compute time. */
          if (f <= .02) {
            continue;
          }
          var px = Math.round((x - halb) * dpr), py = Math.round((y - halb) * dpr);
          ctx2.globalAlpha = IKON_SCHATT * f;
          ctx2.drawImage(sSch, px, py + praegung);
          ctx2.globalAlpha = IKON_DECK * f;
          ctx2.drawImage(sInk, px, py);
        }
      }
      ctx2.globalAlpha = 1;
      ctx2.restore();
    }

    function pos(e) {
      var r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    }

    function scratchTo(p) {
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 34;
      ctx.beginPath();
      if (last) {
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(p.x, p.y);
      } else {
        ctx.moveTo(p.x - .1, p.y);
        ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
      last = p;
      if (++striche % 3 === 0 && !ruhig.matches) {
        span(p.x, p.y);
      }
    }

    var splitterHost = document.getElementById('splitter');
    var ruhig = window.matchMedia('(prefers-reduced-motion: reduce)');
    /* On narrow devices the fill rate is tight, and ninety animations
       running at once would be the most expensive item on the whole page. */
    var SPLITTER_MAX = window.innerWidth < 380 ? 40 : 90;
    var splitter = 0, striche = 0;

    function span(x, y) {
      if (!splitterHost || splitter >= SPLITTER_MAX || !buehneH) {
        return;
      }
      splitter++;

      var el = document.createElement('i');
      var s = 3 + Math.random() * 3;

      var tx = (Math.random() + Math.random() - 1) * 46;
      var ziel = Math.max(2, Math.min(buehneW - s - 2, x + tx));
      /* If you scratch down at the bottom, your shavings no longer fly up. */
      var fall = Math.max(0, buehneH - 6 - s - y);

      el.style.width = el.style.height = s.toFixed(1) + 'px';
      el.style.left = x.toFixed(1) + 'px';
      el.style.top = y.toFixed(1) + 'px';
      el.style.setProperty('--tx', (ziel - x).toFixed(1) + 'px');
      el.style.setProperty('--fall', fall.toFixed(1) + 'px');
      el.style.setProperty('--rot', Math.round(120 + Math.random() * 780) + 'deg');
      el.style.setProperty('--dur', Math.round(620 + Math.random() * 360) + 'ms');
      if (!ruhig.matches) {
        el.style.willChange = 'transform';
      }

      splitterHost.appendChild(el);
    }

    if (splitterHost) {
      splitterHost.addEventListener('animationend', function (e) {
        if (e.animationName === 'splitterFall') {
          e.target.style.willChange = '';
        }
      });
    }

    /* Share of exposed area — every 24th pixel is enough as a sample */
    function clearedRatio() {
      var w = canvas.width, h = canvas.height;
      if (!w || !h) {
        return 0;
      }
      var d = ctx.getImageData(0, 0, w, h).data;
      var clear = 0, total = 0;
      for (var i = 3; i < d.length; i += 4 * 24) {
        total++;
        if (d[i] < 40) {
          clear++;
        }
      }
      return total ? clear / total : 0;
    }

    function reveal() {
      if (revealed) {
        return;
      }
      revealed = true;
      stage.classList.add('is-revealed');

      var prize = stage.querySelector('.sc-prize');
      if (prize) {
        prize.removeAttribute('aria-hidden');
      }
      canvas.setAttribute('aria-hidden', 'true');
      canvas.removeAttribute('role');
      canvas.removeAttribute('aria-label');
      /* The click into the hand as well — where there is one that can buzz. */
      if (navigator.vibrate) {
        navigator.vibrate(12);
      }

      document.documentElement.classList.add('ist-offen');
      bar.style.width = '100%';
      copyBtn.disabled = false;
      copyLbl.textContent = 'Code kopieren';

      var skipHatteFokus = document.activeElement === skipBtn;
      skipBtn.hidden = true;
      if (skipHatteFokus) {
        copyBtn.focus();
      }
      next.classList.add('is-on');

      live.textContent = 'Gutscheincode aufgedeckt: ' + AKTION.code + ' für '
        + gsBis(AKTION.art) + AKTION.rabatt + ' Prozent Rabatt beim Kauf deiner Coins.';

      if (ruhig.matches) {
        for (var i = 0; i < 14; i++) {
          span(buehneW * (.3 + Math.random() * .4), buehneH * .3);
        }
      }
    }

    canvas.addEventListener('pointerdown', function (e) {
      if (revealed) {
        return;
      }
      drawing = true;
      last = null;
      /* From now on the material speaks for itself: the shimmer invitation
         ends with the first touch, forever. */
      stage.classList.add('ist-beruehrt');
      /* On Android the touch ticks along briefly — iOS ignores that
         silently, and it is never load-bearing, only a bonus. */
      if (e.pointerType !== 'mouse' && navigator.vibrate) {
        navigator.vibrate(4);
      }
      if (canvas.setPointerCapture) {
        canvas.setPointerCapture(e.pointerId);
      }
      scratchTo(pos(e));
    });

    canvas.addEventListener('pointermove', function (e) {
      if (!drawing || revealed) {
        return;
      }
      scratchTo(pos(e));
      if (++moves % 6 === 0) {
        /* Net progress relative to the zero mark of the cover layer */
        var netto = (clearedRatio() - basisRatio) / Math.max(.01, 1 - basisRatio);
        if (netto < 0) {
          netto = 0;
        }
        bar.style.width = Math.min(100, Math.round((netto / REVEAL_AT) * 100)) + '%';
        if (netto >= REVEAL_AT) {
          reveal();
        }
      }
    });

    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (ev) {
      canvas.addEventListener(ev, function () {
        drawing = false;
        last = null;
      });
    });

    skipBtn.addEventListener('click', reveal);

    copyBtn.addEventListener('click', function () {
      if (!revealed) {
        return;
      }

      function done() {
        copyLbl.textContent = 'Kopiert';
        copyBtn.classList.add('ist-kopiert');
        live.textContent = 'Code ' + AKTION.code + ' in die Zwischenablage kopiert.';
        setTimeout(function () {
          copyLbl.textContent = 'Code kopieren';
          copyBtn.classList.remove('ist-kopiert');
        }, 2200);
      }

      function fallback() {
        var t = document.createElement('textarea');
        t.value = AKTION.code;
        t.setAttribute('readonly', '');
        t.style.cssText = 'position:absolute;left:-9999px';
        document.body.appendChild(t);
        t.select();
        try {
          document.execCommand('copy');
          done();
        } catch (err) {
          copyLbl.textContent = 'Code: ' + AKTION.code;
        }
        t.remove();
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(AKTION.code).then(done, fallback);
      } else {
        fallback();
      }
    });

    /* The cover layer has to follow every size change of the stage — not just
       a window resize. Otherwise it stays put when the layout only settles
       after the font load or through content loading in later. */
    var rt, lastW = 0, lastH = 0;

    function repaintIfResized() {
      if (revealed) {
        return;
      }
      /* What is observed is the stage (the ResizeObserver below), MEASURED
         is the canvas — the same box that paintCover() is about to paint.
         Otherwise report and painting compared two different numbers. */
      var r = canvas.getBoundingClientRect();
      if (Math.abs(r.width - lastW) < 1 && Math.abs(r.height - lastH) < 1) {
        return;
      }
      lastW = r.width;
      lastH = r.height;
      clearTimeout(rt);
      rt = setTimeout(function () {

        if (!paintCover()) {
          lastW = 0;
          lastH = 0;
          return;
        }
        bar.style.width = '0';
        moves = 0;

        last = null;
      }, 120);
    }

    if (window.ResizeObserver) {
      new ResizeObserver(repaintIfResized).observe(stage);
    } else {
      window.addEventListener('resize', repaintIfResized);
    }

    /* Only draw once the fonts are in place — otherwise the canvas text
       renders in the fallback font. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(paintCover);
    } else {
      window.addEventListener('load', paintCover);
    }
  })();

  (function () {
    var vis = document.querySelector('.gs-where-vis');
    if (!vis) {
      return;
    }

    var feld = vis.querySelector('.gwv-typed');
    var ghost = vis.querySelector('.gwv-ghost');
    var knopf = vis.querySelector('.gwv-apply');
    var pctEl = vis.querySelector('[data-pct-num]');
    if (!feld || !ghost || !knopf || !pctEl) {
      return;
    }

    var code = (feld.textContent || '').trim();
    var pct = (pctEl.textContent || '').trim();
    /* Without a code or without a saving there is nothing to demonstrate.
       Then the static end state stays put, instead of an empty field blinking. */
    if (!code || !pct) {
      return;
    }

    /* The invisible twin holds the field width (see .gwv-slot). */
    ghost.textContent = code;

    var ruhig = window.matchMedia('(prefers-reduced-motion: reduce)');

    var timer = null;
    var laeuft = false;

    function warte(ms, fn) {
      timer = window.setTimeout(fn, ms);
    }

    function anhalten() {
      window.clearTimeout(timer);
      timer = null;
      laeuft = false;
    }

    function zyklus() {
      /* Back to the start: class removed, so the press animation runs from
         the beginning again the next time it is set — a class that is
         already set does not restart any keyframes. */
      knopf.classList.remove('is-pressed');
      vis.classList.remove('is-applied');
      feld.textContent = '';

      vis.classList.remove('ist-getippt');

      var i = 0;

      function tippe() {
        if (i >= code.length) {
          warte(400, druecken);
          return;
        }
        feld.textContent = code.slice(0, ++i);
        vis.classList.add('ist-getippt');
        /* Unevenly timed. Exactly equal intervals sound like a ticker,
           the randomness between 90 and 130 ms sounds like a hand. */
        warte(90 + Math.random() * 40, tippe);
      }

      warte(750, tippe);
    }

    function druecken() {
      knopf.classList.add('is-pressed');
      /* The price jumps at the moment of impact, not afterwards: the button
         animation is past its lowest point at 220 ms, 150 ms hits the
         compression. Cause and effect coincide. */
      warte(150, springen);
    }

    function springen() {
      vis.classList.add('is-applied');
      /* The result has to be allowed to stand — anyone who only looks now
         should read the reduced price, not the next run already. */
      warte(2500, zyklus);
    }

    function starten() {
      if (laeuft || ruhig.matches) {
        return;
      }
      laeuft = true;
      vis.classList.add('gwv-run');
      zyklus();
    }

    function pausieren() {
      anhalten();
      knopf.classList.remove('is-pressed');
      vis.classList.remove('gwv-run');
      vis.classList.remove('is-applied');
      feld.textContent = code;
    }

    if (window.IntersectionObserver) {
      new IntersectionObserver(function (eintraege) {
        for (var i = 0; i < eintraege.length; i++) {
          if (eintraege[i].isIntersecting) {
            starten();
          } else {
            pausieren();
          }
        }
      }, { threshold: .4 }).observe(vis);
    } else {
      /* Without an observer, better to let it run permanently than show nothing —
         the block sits far down and is only seen when in view anyway. */
      starten();
    }

    /* Anyone who flips the setting while it is running immediately gets
       the right thing: with reduced motion the finished state without the
       way there, otherwise the demo. */
    var wechsel = function () {
      if (ruhig.matches) {
        pausieren();
      } else if (!laeuft) {
        starten();
      }
    };
    if (ruhig.addEventListener) {
      ruhig.addEventListener('change', wechsel);
    } else if (ruhig.addListener) {
      ruhig.addListener(wechsel);
    }
  })();

  (function () {
    var items = document.querySelectorAll('.gs-faq details');
    if (!items.length) {
      return;
    }

    var fragen = [];
    Array.prototype.forEach.call(items, function (d) {
      var s = d.querySelector('summary');
      var a = d.querySelector('p');
      if (!s || !a) {
        return;
      }

      var k = a.cloneNode(true);
      Array.prototype.forEach.call(k.querySelectorAll('[hidden]'), function (h) {
        h.parentNode.removeChild(h);
      });
      fragen.push({
        '@type': 'Question',
        name: s.textContent.replace(/\s+/g, ' ').trim(),
        acceptedAnswer: {
          '@type': 'Answer',
          text: k.textContent.replace(/\s+/g, ' ').trim()
        }
      });
    });
    if (!fragen.length) {
      return;
    }

    var el = document.createElement('script');
    el.type = 'application/ld+json';
    el.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: fragen
    });
    document.head.appendChild(el);
  })();

  /* ============================================================
     AMBIENT DECORATION — motif comes from the season's <template>
     ============================================================ */
  (function () {
    var root = document.getElementById('deco');
    if (!root) {
      return;
    }

    var season = GS_ROOT.getAttribute('data-season') || 'basis';
    var motiv = MOTIVE[season] || MOTIVE.basis;
    if (!motiv) {
      return;
    }

    var cs = getComputedStyle(GS_ROOT);
    var c1 = cs.getPropertyValue('--sn-deco-1').trim();
    var c2 = cs.getPropertyValue('--sn-deco-2').trim();

    for (var i = 0; i < 14; i++) {
      var el = document.createElement('i');
      var dur = 13 + Math.random() * 10;
      /* Drift only in the edge zones: the headings are gradient text
         (background-clip:text) and therefore transparent — decoration
         behind them would shine right through the letters. */
      var links = (i % 2 === 0);
      var bahn = links ? Math.random() * 17 : 83 + Math.random() * 17;
      el.style.left = bahn.toFixed(2) + 'vw';
      el.style.animationDuration = dur.toFixed(1) + 's';
      el.style.animationDelay = (-Math.random() * dur).toFixed(1) + 's';
      el.style.color = (i % 3 === 0) ? c2 : c1;
      el.innerHTML = motiv;
      root.appendChild(el);
    }
  })();

  /* ---- was an inline <script> inside the markup ---- */

  (function () {
    /* Was document.currentScript.previousElementSibling — this block ran as an
       inline <script> right after the element. Bundled, currentScript is the
       bundle, so the element is looked up on the page root instead. */
    var steps = GS_ROOT.querySelector('.gs-steps');
    if (!steps) {
      return;
    }
    if (!window.IntersectionObserver) {
      return;
    }

    var ruhig = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (ruhig.matches) {
      return;
    }

    steps.classList.add('ist-dunkel');

    new IntersectionObserver(function (eintraege, beobachter) {
      for (var i = 0; i < eintraege.length; i++) {
        if (!eintraege[i].isIntersecting) {
          continue;
        }
        steps.classList.remove('ist-dunkel');
        steps.classList.add('ist-erhellt');

        beobachter.unobserve(eintraege[i].target);
      }
    }, { threshold: .25 }).observe(steps);
  })();

}

export default {
  name: 'GutscheincodePage',
  mixins: [landingChromeMixin],
  components: { CookieBanner, SignupModal },
  data() {
    return {
      discount: DISCOUNT,
      code: CODE,
      isLoggedIn: !document.body.classList.contains('body-guest'),
    };
  },
  computed: {
    portalHref() {
      return zRoute('home');
    },
  },
  mounted() {
    initGutscheincodePage(this.$el);
    this.checkLoggedIn();
  },
  methods: {
    async checkLoggedIn() {
      let resp;
      try {
        resp = await axios.get(zRoute('ajax.auth.users.show'));
      } catch (e) {
        return;
      }
      this.isLoggedIn = !!(resp && resp.status !== 204 && resp.data && resp.data.data);
    },
  },
};
</script>
